import React, { useState, useEffect, useMemo } from 'react';
import { Luna } from './Luna';
import styles from './styles/StepAssets.module.css';
import inputStyles from './styles/OnboardingInput.module.css';
import sharedStyles from './styles/BrandSignup.module.css';

const MAX_IMAGES = 15;

const StepAssets = ({ brandSignup, onComplete, onSkipToDashboard, loading, error, onBack, canGoBack }) => {
  const [formData, setFormData] = useState({
    reference_link: brandSignup.reference_link || ''
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState(brandSignup.reference_images || []);
  const [formErrors, setFormErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    // Pre-fill if we have step data
    const stepData = brandSignup.step_data?.['assets'];
    if (stepData) {
      setFormData(prev => ({
        ...prev,
        reference_link: stepData.reference_link || prev.reference_link
      }));
    }
  }, [brandSignup.step_data]);

  const validateForm = () => {
    const errors = {};

    // Only validate if user has provided a reference link
    if (formData.reference_link.trim() && !isValidUrl(formData.reference_link)) {
      errors.reference_link = 'Please enter a valid URL';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if user has provided either images or a link
  const hasAssets = () => {
    return uploadedFiles.length > 0 || existingImages.length > 0 || formData.reference_link.trim().length > 0;
  };

  // Total images = existing + newly uploaded
  const totalImageCount = existingImages.length + uploadedFiles.length;

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleComplete = () => {
    if (!validateForm()) {
      return;
    }

    // Create FormData for file uploads
    const formDataToSubmit = new FormData();

    // Always include reference link (empty string if not provided)
    formDataToSubmit.append('step_data[reference_link]', formData.reference_link || '');

    // Add newly uploaded files
    uploadedFiles.forEach((file, index) => {
      formDataToSubmit.append('reference_images[]', file);
    });

    // Track which existing images to keep (by their IDs)
    existingImages.forEach((image) => {
      formDataToSubmit.append('keep_image_ids[]', image.id);
    });

    onComplete(formDataToSubmit, { isFileUpload: true });
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - totalImageCount;

    if (remainingSlots <= 0) {
      setFormErrors(prev => ({
        ...prev,
        images: `Maximum of ${MAX_IMAGES} images allowed`
      }));
      return;
    }

    const filesToAdd = files.slice(0, remainingSlots);
    setUploadedFiles(prevFiles => [...prevFiles, ...filesToAdd]);

    // Show warning if some files were not added
    if (files.length > remainingSlots) {
      setFormErrors(prev => ({
        ...prev,
        images: `Only ${remainingSlots} image(s) added. Maximum of ${MAX_IMAGES} images allowed`
      }));
    } else {
      // Clear error if successful
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.images;
        return newErrors;
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    const remainingSlots = MAX_IMAGES - totalImageCount;

    if (remainingSlots <= 0) {
      setFormErrors(prev => ({
        ...prev,
        images: `Maximum of ${MAX_IMAGES} images allowed`
      }));
      return;
    }

    if (files.length > 0) {
      const filesToAdd = files.slice(0, remainingSlots);
      setUploadedFiles(prevFiles => [...prevFiles, ...filesToAdd]);

      // Show warning if some files were not added
      if (files.length > remainingSlots) {
        setFormErrors(prev => ({
          ...prev,
          images: `Only ${remainingSlots} image(s) added. Maximum of ${MAX_IMAGES} images allowed`
        }));
      } else {
        // Clear error if successful
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.images;
          return newErrors;
        });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const removeFile = (index) => {
    setUploadedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    // Clear error when removing a file
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.images;
      return newErrors;
    });
  };

  const removeExistingImage = (imageId) => {
    setExistingImages(prevImages => prevImages.filter(img => img.id !== imageId));
    // Clear error when removing an image
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.images;
      return newErrors;
    });
  };

  // Memoize Luna messages and component to prevent re-typing on form changes
  const lunaMessages = useMemo(() => [
    "A picture is worth a thousand words!",
    "Please upload an image or product link -- can be actual or inspirational .",
  ], []);

  const memoizedLuna = useMemo(() =>
    <Luna lunaDialogs={lunaMessages} />,
    [lunaMessages]
  );

  return (
    <div className={styles.stepContainer}>
      {memoizedLuna}

      <div className={styles.formContainer}>
        <form onSubmit={(e) => { e.preventDefault(); handleComplete(); }}>
          <div className={styles.formField}>
            <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Link to the product or reference image</label>
            <input
              className={`${styles.linkInput} ${formErrors.reference_link ? styles.linkInputError : ''}`}
              type="url"
              placeholder="Link to the product or reference image"
              value={formData.reference_link}
              onChange={(e) => handleInputChange('reference_link', e.target.value)}
              disabled={loading}
            />
            </div>
           
           
            {formErrors.reference_link && (
              <p className={styles.errorMessage}>{formErrors.reference_link}</p>
            )}
          </div>

          <div className={styles.formField}>
            {formErrors.images && (
              <p className={styles.errorMessage}>{formErrors.images}</p>
            )}
            <div
              className={`${ totalImageCount === 0 ? styles.uploadAreaEmpty : styles.uploadArea } ${isDragOver ? styles.dragOver : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {totalImageCount === 0 ? (
                <div className={styles.emptyUploadArea}>
                  <div className={styles.uploadIconContainer}>
                    <i className={`ph ph-upload-simple ${styles.uploadIcon}`}></i>
                  </div>
                  <p className={styles.dropText}>Drop your image or files here</p>
                  <label className={styles.browseButton}>
                    <input
                      className={styles.hiddenFileInput}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileUpload}
                      disabled={loading}
                    />
                    Browse for files
                  </label>
                </div>
              ) : (
                <div className={styles.uploadGrid}>
                  {/* Display existing images from homepage upload */}
                  {existingImages.map((image) => (
                    <div key={`existing-${image.id}`} className={styles.imagePreview}>
                      <button
                        onClick={() => removeExistingImage(image.id)}
                        type="button"
                        className={styles.removeButton}
                      >
                        <i className={`ph-bold ph-x ${styles.removeIcon}`}></i>
                      </button>
                      <img
                        src={image.thumb_url}
                        alt={image.filename}
                        className={styles.previewImage}
                      />
                    </div>
                  ))}

                  {/* Display newly uploaded files */}
                  {uploadedFiles.map((file, index) => {
                    const previewUrl = URL.createObjectURL(file);
                    return (
                      <div key={`new-${index}`} className={styles.imagePreview}>
                        <button
                          onClick={() => removeFile(index)}
                          type="button"
                          className={styles.removeButton}
                        >
                          <i className={`ph-bold ph-x ${styles.removeIcon}`}></i>
                        </button>
                        <img
                          src={previewUrl}
                          alt={file.name}
                          className={styles.previewImage}
                          onLoad={() => {
                            URL.revokeObjectURL(previewUrl);
                          }}
                        />
                      </div>
                    );
                  })}

                  {totalImageCount < MAX_IMAGES && (
                    <div className={styles.uploadButton}>
                      <label style={{ cursor: 'pointer', textAlign: 'center', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <input
                          className={styles.hiddenFileInput}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileUpload}
                          disabled={loading}
                        />
                        <div className={styles.uploadIcon}>
                          <i className={`ph-bold ph-plus ${styles.uploadIcon}`}></i>
                        </div>
                        <div className={styles.uploadText}>
                          Click to add or<br />drag & drop<br />image here
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <p className={styles.skipText}>You can skip this step until it's time to write your RFQ</p>
            </div>
          </div>

          <button
            type="submit"
            className={styles.continueButton}
            disabled={loading}
          >
            Continue
          </button>

          {canGoBack && (
            <a
              className={sharedStyles.backLink}
              onClick={(e) => {
                e.preventDefault();
                onBack();
              }}
            >
              ← Back
            </a>
          )}

          {onSkipToDashboard && (
            <button
              type="button"
              className={sharedStyles.skipLink}
              onClick={onSkipToDashboard}
              disabled={loading}
            >
              I'll set up my project later. Take me to the Dashboard
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default StepAssets;
